/**
 * Minimal Lucide icons bundle for article pages
 * Includes: article UI (bookmark, copy, download, file-text), client advisor (CheckCircle, X),
 * and admin toolbar icons so "Edit Article" bar renders correctly when admin views an article.
 */

import {
  createIcons,
  Archive,
  ArrowLeftRight,
  Bookmark,
  Box,
  CalendarCheck,
  ChartLine,
  CheckCircle,
  CircleHelp,
  Copy,
  Download,
  ExternalLink,
  FilePen,
  FileText,
  Gift,
  Globe,
  GraduationCap,
  Handshake,
  Home,
  MessagesSquare,
  MessageSquare,
  Navigation,
  Lightbulb,
  PartyPopper,
  Pencil,
  Puzzle,
  Save,
  SquarePen,
  Sparkles,
  Trophy,
  User,
  UserPen,
  UserPlus,
  Video,
  X,
  Zap
} from 'lucide'

const globalScope = typeof window !== 'undefined' ? window : globalThis

if (!globalScope.__lucideArticleRendererInitialized && typeof document !== 'undefined') {
  globalScope.__lucideArticleRendererInitialized = true

  let renderScheduled = false

  const renderIcons = () => {
    renderScheduled = false
    createIcons({
      icons: {
        Archive,
        ArrowLeftRight,
        Bookmark,
        Box,
        CalendarCheck,
        ChartLine,
        CheckCircle,
        CircleHelp,
        Copy,
        Download,
        ExternalLink,
        FilePen,
        FileText,
        Gift,
        Globe,
        GraduationCap,
        Handshake,
        Home,
        MessagesSquare,
        MessageSquare,
        Navigation,
        Lightbulb,
        PartyPopper,
        Pencil,
        Puzzle,
        Save,
        SquarePen,
        Sparkles,
        Trophy,
        User,
        UserPen,
        UserPlus,
        Video,
        X,
        Zap
      }
    })
  }

  const scheduleRender = () => {
    if (renderScheduled) return
    renderScheduled = true
    ;(globalScope.requestAnimationFrame || globalScope.setTimeout)(renderIcons)
  }

  const hasPendingIcons = (root = document) =>
    root.querySelector?.('[data-lucide]:not(svg)') ||
    root.querySelector?.('svg[data-lucide]:not(.lucide)')

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-lucide') {
        scheduleRender()
        return
      }

      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && hasPendingIcons(node)) {
            scheduleRender()
            return
          }
        }
      }
    }
  })

  const start = () => {
    scheduleRender()
    observer.disconnect()
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-lucide']
      })
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  document.addEventListener('turbo:load', start)
  document.addEventListener('turbolinks:load', start)

  globalScope.refreshLucideIcons = () => scheduleRender()
}

